"use client";

import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  Checkbox,
  CopyButton,
  PillGroup,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  INPUT_MONO,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  type KeyAction,
  Keypad,
} from "@/components/tools/arithmetic/scientific-keys";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { formatNumber } from "@/lib/format-number";
import { t } from "@/lib/i18n/interpolate";
import {
  type AngleMode,
  type CalcErrorCode,
  type DisplayMode,
  displayNumber,
  evaluateExpression,
} from "@/lib/tool-kit/arithmetic/scientific";
import {
  INITIAL_SESSION,
  reduceSession,
  type SessionOptions,
} from "@/lib/tool-kit/arithmetic/scientific-session";

/** English strings. A translated content file overrides these through `ui`. */
export const SCIENTIFIC_CALCULATOR_STRINGS = {
  expressionLabel: "Expression",
  expressionHint:
    "Click here to type, or use the keys. Enter works out the answer and Esc clears it. Use a decimal point; commas separate arguments.",
  placeholder: "sin(30) + 2^3 × 4",
  angleLabel: "Angle unit",
  angleDeg: "Degrees",
  angleRad: "Radians",
  angleGrad: "Gradians",
  displayLabel: "Number display",
  displayNormal: "Normal",
  displayScientific: "Scientific",
  implicitTight: "Treat 2π as one unit: 1 ÷ 2π = 1 ÷ (2π)",
  results: "Results",
  answer: "Answer",
  preview: "= {value}",
  memoryLabel: "Memory",
  memoryEmpty: "empty",
  groupMemory: "Memory keys",
  groupFunctions: "Function keys",
  groupNumbers: "Number keys",
  controls: {
    second: "2nd",
    ac: "AC",
    del: "DEL",
    ans: "Ans",
    mc: "MC",
    mr: "MR",
    mplus: "M+",
    mminus: "M−",
    ms: "MS",
  },
  keyNames: {
    second: "Second functions",
    sin: "Sine",
    asin: "Inverse sine",
    cos: "Cosine",
    acos: "Inverse cosine",
    tan: "Tangent",
    atan: "Inverse tangent",
    ln: "Natural logarithm",
    exp: "e to the power",
    log: "Common logarithm",
    tenPower: "10 to the power",
    pi: "Pi",
    e: "Euler’s number e",
    square: "Square",
    cube: "Cube",
    power: "To the power",
    sqrt: "Square root",
    cbrt: "Cube root",
    reciprocal: "Reciprocal",
    open: "Open bracket",
    close: "Close bracket",
    factorial: "Factorial",
    choose: "Combinations, n choose r",
    percent: "Percent",
    abs: "Absolute value",
    times10: "Times ten to the power",
    clear: "Clear all",
    backspace: "Delete",
    ans: "Previous answer",
    divide: "Divide",
    multiply: "Multiply",
    subtract: "Subtract",
    add: "Add",
    point: "Decimal point",
    negate: "Negative sign",
    equals: "Equals",
    mc: "Memory clear",
    mr: "Memory recall",
    mplus: "Add to memory",
    mminus: "Subtract from memory",
    ms: "Store in memory",
  },
  copy: "Copy answer",
  copied: "Copied!",
  historyHeading: "History",
  historyEmpty: "Answers you work out appear here.",
  historyClear: "Clear history",
  historyEntry: "{expression} = {answer}",
  useExpression: "Reuse",
  useAnswer: "Use answer",
  useExpressionLabel: "Reuse the expression {expression}",
  useAnswerLabel: "Use the answer to {expression}",
  errors: {
    empty: "Type an expression first.",
    "too-long": "That expression is too long.",
    "too-deep": "Too many brackets are nested inside each other.",
    "bad-character": "“{token}” is not allowed here.",
    "unexpected-token": "“{token}” is not expected at that point.",
    "unexpected-end":
      "The expression ends too soon: it needs a number or a bracket after “{token}”.",
    "unknown-name":
      "“{token}” is not a function or constant this calculator knows.",
    "wrong-arguments": "{token} needs a different number of values.",
    "divide-by-zero": "Division by zero is undefined.",
    domain:
      "That is outside the function’s domain (for example √ of a negative number, or ln of 0).",
    "undefined-tangent": "The tangent is undefined at that angle.",
    overflow: "The result is too large to represent.",
  },
  notePrecision:
    "Answers use IEEE 754 double precision and are shown to 12 significant digits.",
} as const;

type Strings = typeof SCIENTIFIC_CALCULATOR_STRINGS;

function errorText(
  code: CalcErrorCode,
  token: string,
  strings: Strings
): string {
  return t(strings.errors[code], { token });
}

function History({
  entries,
  strings,
  onReuse,
  onClear,
}: {
  entries: readonly {
    id: number;
    expression: string;
    pretty: string;
    plain: string;
  }[];
  strings: Strings;
  onReuse: (text: string) => void;
  onClear: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className={WIDGET_H3}>{strings.historyHeading}</h3>
        {entries.length > 0 ? (
          <button className={BUTTON_SMALL} onClick={onClear} type="button">
            {strings.historyClear}
          </button>
        ) : null}
      </div>
      {entries.length === 0 ? (
        <p className="text-muted-foreground text-sm">{strings.historyEmpty}</p>
      ) : (
        <ol className="space-y-2">
          {entries.map((entry) => (
            <li
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2"
              key={entry.id}
            >
              <span className="break-all font-mono text-foreground text-sm">
                {t(strings.historyEntry, {
                  expression: entry.expression,
                  answer: entry.pretty,
                })}
              </span>
              <span className="flex gap-2">
                <button
                  aria-label={t(strings.useExpressionLabel, {
                    expression: entry.expression,
                  })}
                  className={BUTTON_SMALL}
                  onClick={() => onReuse(entry.expression)}
                  type="button"
                >
                  {strings.useExpression}
                </button>
                <button
                  aria-label={t(strings.useAnswerLabel, {
                    expression: entry.expression,
                  })}
                  className={BUTTON_SMALL}
                  onClick={() => onReuse(entry.plain)}
                  type="button"
                >
                  {strings.useAnswer}
                </button>
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function ScientificCalculatorWidget() {
  const strings = useToolUi(SCIENTIFIC_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const mark = formatNumber(locale, 1.5).charAt(1);
  const [session, dispatch] = useReducer(reduceSession, INITIAL_SESSION);
  const [angle, setAngle] = useState<AngleMode>("deg");
  const [display, setDisplay] = useState<DisplayMode>("normal");
  const [implicitTight, setImplicitTight] = useState(false);
  const [second, setSecond] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const options: SessionOptions = useMemo(
    () => ({ angle, implicitTight, display }),
    [angle, implicitTight, display]
  );
  const live = useMemo(
    () =>
      evaluateExpression(session.expression, {
        angle,
        ans: session.ans,
        implicitTight,
      }),
    [session.expression, session.ans, angle, implicitTight]
  );

  // Keep the field's caret where the keys left it.
  useEffect(() => {
    const el = inputRef.current;
    if (
      el &&
      (el.selectionStart !== session.selectionStart ||
        el.selectionEnd !== session.selectionEnd)
    ) {
      el.setSelectionRange(session.selectionStart, session.selectionEnd);
    }
  }, [session.selectionStart, session.selectionEnd]);

  const press = (action: KeyAction, pointer: string) => {
    if (pointer === "mouse") {
      // A mouse user can carry on typing; a touch user is not met with the OS keyboard.
      inputRef.current?.focus({ preventScroll: true });
    }
    switch (action.type) {
      case "insert":
        dispatch({ type: "insert", text: action.text, kind: action.kind });
        break;
      case "second":
        setSecond((on) => !on);
        break;
      case "equals":
        dispatch({ type: "equals", options });
        break;
      case "memory":
        dispatch({ type: "memory", operation: action.operation, options });
        break;
      case "clear":
        dispatch({ type: "clear" });
        break;
      default:
        dispatch({ type: "backspace" });
    }
  };

  const showPretty = (pretty: string) => pretty.replace(".", mark);
  const answer = session.justEvaluated ? session.history[0] : undefined;
  const previewValue = live.ok
    ? showPretty(displayNumber(live.value, display).pretty)
    : null;
  const memoryText =
    session.memory === 0
      ? strings.memoryEmpty
      : showPretty(displayNumber(session.memory, display).pretty);

  return (
    <div className="space-y-4">
      <form
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault();
          dispatch({ type: "equals", options });
        }}
      >
        <label className={LABEL} htmlFor="sc-expression">
          {strings.expressionLabel}
        </label>
        <input
          aria-describedby="sc-result"
          autoComplete="off"
          className={`${INPUT_MONO} text-lg`}
          id="sc-expression"
          onChange={(event) =>
            dispatch({
              type: "edit",
              expression: event.target.value,
              start: event.target.selectionStart ?? event.target.value.length,
              end: event.target.selectionEnd ?? event.target.value.length,
            })
          }
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              dispatch({ type: "clear" });
            }
          }}
          onSelect={(event) => {
            const el = event.currentTarget;
            const start = el.selectionStart ?? 0;
            const end = el.selectionEnd ?? 0;
            if (
              start !== session.selectionStart ||
              end !== session.selectionEnd
            ) {
              dispatch({ type: "select", start, end });
            }
          }}
          placeholder={strings.placeholder}
          ref={inputRef}
          spellCheck={false}
          type="text"
          value={session.expression}
        />
        <p className="text-muted-foreground text-xs">
          {strings.expressionHint}
        </p>
      </form>

      <section aria-live="polite" className="space-y-2" id="sc-result">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {session.error ? (
          <p
            className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-destructive text-sm"
            role="alert"
          >
            {errorText(session.error.code, session.error.token, strings)}
          </p>
        ) : null}
        {answer ? (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
            <p className="font-mono font-semibold text-2xl text-foreground">
              {strings.answer}: {showPretty(answer.pretty)}
            </p>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copy}
              small
              text={answer.plain}
            />
          </div>
        ) : null}
        {!answer && previewValue !== null ? (
          <p className="font-mono text-muted-foreground text-xl">
            {t(strings.preview, { value: previewValue })}
          </p>
        ) : null}
        <p className="text-muted-foreground text-xs">
          {strings.memoryLabel}: {memoryText}
        </p>
      </section>

      <div className="grid gap-4 sm:grid-cols-3">
        <PillGroup
          label={strings.angleLabel}
          name="sc-angle"
          onChange={setAngle}
          options={[
            { value: "deg", label: strings.angleDeg },
            { value: "rad", label: strings.angleRad },
            { value: "grad", label: strings.angleGrad },
          ]}
          value={angle}
        />
        <PillGroup
          label={strings.displayLabel}
          name="sc-display"
          onChange={setDisplay}
          options={[
            { value: "normal", label: strings.displayNormal },
            { value: "scientific", label: strings.displayScientific },
          ]}
          value={display}
        />
        <div className="self-end">
          <Checkbox
            checked={implicitTight}
            label={strings.implicitTight}
            onChange={setImplicitTight}
          />
        </div>
      </div>

      <Keypad onPress={press} second={second} strings={strings} />

      <History
        entries={session.history}
        onClear={() => dispatch({ type: "clearHistory" })}
        onReuse={(text) => dispatch({ type: "reuse", text })}
        strings={strings}
      />
      <p className="text-muted-foreground text-xs">{strings.notePrecision}</p>
    </div>
  );
}
